import { FC, useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useUnleashContext } from "@unleash/proxy-client-react";
import * as Sentry from "@sentry/react";
import { ProtectedRoute } from "./routes/ProtectedRoute";
import { ViewAccessCheck } from "./routes/ViewAccessCheck";
import { ExecutionLogs } from "../pages/execution-log/ExecutionLogs";
import { ScheduledJobs } from "../pages/schedule/classic/ScheduledJobs";
import { ScheduledJobsItem } from "../pages/schedule/classic/ScheduledJobsItem";
import { OffHours } from "../pages/schedule/off-hours/OffHours";
import { OffHoursItem } from "../pages/schedule/off-hours/OffHoursItem";
import { Integrations } from "../pages/integrations/Integrations";
import { IntegrationItem } from "../pages/integrations/IntegrationItem";
import { OffHoursRecommendations } from "../pages/recommendations/components/off-hours-recommendations/OffHoursRecommendations";
import { CloudChat } from "../pages/cloudchat/CloudChat";
import { RedirectPage } from "../pages/redirect/RedirectPage";
import { RightsizingRecommendations } from "../pages/recommendations/components/rightsizing-recommendations/RightsizingRecommendations";
import { ResourceExplorer } from "../pages/resource-explorer/ResourceExplorer";
import { IntegrationsSlackAppConnection } from "../pages/integrations/IntegrationsSlackAppConnection";
import { Budgets } from "../pages/budgets/Budgets";
import { BudgetItem } from "../pages/budgets/BudgetItem";
import { NotFound } from "../error-pages/NotFound";
import { AccessDenied } from "../error-pages/AccessDenied";
import { useAppSelector } from "../../store/hooks";
import { currentUserIdSelector } from "../../store/profile/profileSelectors";
import { ScheduleResourceProtection } from "../pages/schedule/resource-protection/ScheduleResourceProtection";
import { ProviderAccounts } from "../pages/accounts-group/ProviderAccounts";
import { AccountsOldUrlsTmpGuard } from "../pages/accounts-group/AccountsOldUrlsTmpGuard";
import { Dashboard } from "../pages/dashboard/Dashboard";
import { currentOrganizationSelector } from "../../store/organizations/organizationsSelectors";
import { MainLayout } from "../layout/MainLayout";
import { Categories } from "../pages/categories/Categories";
import { SmartTagPreferences } from "../pages/settings/components/smart-tag/SmartTagPreferences";
import { Users } from "../pages/settings/components/users/Users";
import { Billing } from "../pages/settings/components/billing/Billing";
import { Organization } from "../pages/settings/components/organization/Organization";
import { ApiKeysList } from "../pages/settings/components/api-keys/ApiKeysList";
import { Profile } from "../pages/settings/components/profile/Profile";
import { AuthAndSSO } from "../pages/settings/components/auth/AuthAndSSO";
import { AccountSetup } from "../pages/account-setup/AccountSetup";
import { AccountV2 } from "../pages/account/AccountV2";
import { UtilizationAndCoverage } from "../pages/commtiments/utilization-and-coverage/UtilizationAndCoverage";
import { EmailNotifications } from "../pages/settings/components/email-notifications/EmailNotifications";
import { CommitmentsRecommendations } from "../pages/commtiments/recommendations/CommitmentsRecommendations";
import { SingleRightsizingRecommendation } from "../pages/recommendations/components/single-rightsizing-recommendation/SingleRightsizingRecommendation";
import { Dimensions } from "../pages/dimensions/Dimensions";

const SentryRoutes = Sentry.withSentryRouting(Routes);

export const AppRouter: FC = () => {
  const updateContext = useUnleashContext();
  const userId = useAppSelector(currentUserIdSelector);
  const organization = useAppSelector(currentOrganizationSelector);

  useEffect(() => {
    updateContext({
      userId,
      properties: { organizationId: organization?.id ?? "" },
    });
  }, [userId, updateContext, organization?.id]);

  if (!userId) {
    return null;
  }

  const redirectUrl = "/settings/access-denied";

  return (
    <SentryRoutes>
      <Route
        path="/account/setup"
        element={
          <ProtectedRoute>
            <ViewAccessCheck subject="account">
              <AccountSetup />
            </ViewAccessCheck>
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<RedirectPage />} />
        <Route path="not-found" element={<NotFound />} />
        <Route path="/access-denied" element={<AccessDenied />} />

        <Route
          path="/aws/:accountId/*"
          element={
            <ViewAccessCheck subject="account">
              <AccountV2 />
            </ViewAccessCheck>
          }
        />
        <Route
          path="/gcp/:accountId/*"
          element={
            <ViewAccessCheck subject="account">
              <AccountV2 />
            </ViewAccessCheck>
          }
        />
        <Route
          path="/azure/:accountId/*"
          element={
            <ViewAccessCheck subject="account">
              <AccountV2 />
            </ViewAccessCheck>
          }
        />

        <Route
          path="/dashboards"
          element={<ViewAccessCheck subject="dashboard" />}
        >
          <Route path=":dashboardId" element={<Dashboard />} />
        </Route>
        <Route
          path="/resource-explorer"
          element={<ViewAccessCheck subject="resource-explorer" />}
        >
          <Route index element={<ResourceExplorer />} />
          <Route path=":viewId" element={<ResourceExplorer />} />
        </Route>

        <Route path="/aws/orgs/:orgId" element={<AccountsOldUrlsTmpGuard />} />
        <Route path="/aws" element={<AccountsOldUrlsTmpGuard />} />
        <Route path="/gcp" element={<AccountsOldUrlsTmpGuard />} />
        <Route path="/azure" element={<AccountsOldUrlsTmpGuard />} />

        <Route
          path="/:provider/:orgId/accounts"
          element={
            <ViewAccessCheck subject="account">
              <ProviderAccounts />
            </ViewAccessCheck>
          }
        />

        <Route path="/cloudchat" element={<CloudChat />} />

        <Route
          path="/schedule"
          element={<ViewAccessCheck subject="schedule" />}
        >
          <Route path="classic" element={<ScheduledJobs />} />
          <Route path="classic/create" element={<ScheduledJobsItem />} />
          <Route path="classic/edit/:id" element={<ScheduledJobsItem />} />

          <Route path="off-hours" element={<OffHours />} />
          <Route path="off-hours/create" element={<OffHoursItem />} />
          <Route path="off-hours/edit/:id" element={<OffHoursItem />} />
        </Route>

        <Route
          path="schedule-resource-protection/:scheduleId/:batchId"
          element={<ScheduleResourceProtection />}
        />

        <Route
          path="/recommendations"
          element={<ViewAccessCheck subject="recommendation" />}
        >
          <Route path="off-hours" element={<OffHoursRecommendations />} />
          <Route path="off-hours/:type" element={<OffHoursRecommendations />} />
          <Route path="rightsizing" element={<RightsizingRecommendations />} />
          <Route
            path="rightsizing/:recommendationId"
            element={<SingleRightsizingRecommendation />}
          />
        </Route>

        <Route
          path="/integrations"
          element={
            <ViewAccessCheck subject="integration">
              <Integrations />
            </ViewAccessCheck>
          }
        />
        <Route
          path="/integrations/create"
          element={
            <ViewAccessCheck subject="integration">
              <IntegrationItem />
            </ViewAccessCheck>
          }
        />
        <Route
          path="/integrations/slack-app-connection"
          element={
            <ViewAccessCheck subject="integration">
              <IntegrationsSlackAppConnection />
            </ViewAccessCheck>
          }
        />
        <Route
          path="/integrations/edit/:type/:id"
          element={
            <ViewAccessCheck subject="integration">
              <IntegrationItem />
            </ViewAccessCheck>
          }
        />

        <Route path="/budgets" element={<ViewAccessCheck subject="budget" />}>
          <Route path="" element={<Budgets />} />
          <Route path="create" element={<BudgetItem />} />
          <Route path="edit/:id" element={<BudgetItem />} />
        </Route>

        <Route
          path="/execution-log"
          element={<ViewAccessCheck subject="account" />}
        >
          <Route path="" element={<ExecutionLogs />} />
          <Route path=":id" element={<ExecutionLogs />} />
        </Route>

        <Route
          path="/commitments"
          element={<ViewAccessCheck subject="commitment" />}
        >
          <Route
            path="utilization-and-coverage/:orgId"
            element={<UtilizationAndCoverage />}
          />
          <Route
            path="utilization-and-coverage"
            element={<UtilizationAndCoverage />}
          />

          <Route
            path="recommendations/:orgId"
            element={<CommitmentsRecommendations />}
          />
          <Route
            path="recommendations"
            element={<CommitmentsRecommendations />}
          />
        </Route>

        <Route path="/settings">
          <Route path="access-denied" element={<AccessDenied />} />

          <Route
            path="categories"
            element={
              <ViewAccessCheck subject="category" redirectUrl={redirectUrl}>
                <Categories />
              </ViewAccessCheck>
            }
          />
          <Route
            path="dimensions"
            element={
              <ViewAccessCheck subject="category" redirectUrl={redirectUrl}>
                <Dimensions />
              </ViewAccessCheck>
            }
          />
          <Route
            path="smart-tag"
            element={
              <ViewAccessCheck subject="account" redirectUrl={redirectUrl}>
                <SmartTagPreferences />
              </ViewAccessCheck>
            }
          />
          <Route
            path="users"
            element={
              <ViewAccessCheck subject="user" redirectUrl={redirectUrl}>
                <Users />
              </ViewAccessCheck>
            }
          />
          <Route
            path="billing"
            element={
              <ViewAccessCheck subject="billing" redirectUrl={redirectUrl}>
                <Billing />
              </ViewAccessCheck>
            }
          />
          <Route
            path="organization"
            element={
              <ViewAccessCheck subject="organisation" redirectUrl={redirectUrl}>
                <Organization />
              </ViewAccessCheck>
            }
          />
          <Route
            path="api-keys"
            element={
              <ViewAccessCheck subject="api-key" redirectUrl={redirectUrl}>
                <ApiKeysList />
              </ViewAccessCheck>
            }
          />
          <Route path="profile" element={<Profile />} />
          <Route path="notifications" element={<EmailNotifications />} />
          <Route path="auth" element={<AuthAndSSO />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </SentryRoutes>
  );
};
